/* eslint-disable react-hooks/exhaustive-deps */
import { API_ROOT } from '@/module/common';
import { CONSOLIDATION_API } from '@/module/corporate-consolidation-report/constants';
import CRConsolidationReportPDF from '@/new-components/CRConsolidationReportPDF';
import CRReportPDF from '@/new-components/CRReportPDF';
import customToast from '@/new-components/CustomNotification';
import { get, isEmpty } from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const DualReportDownload = (props) => {
  const accessToken = useSelector((state) => state.auth.accessToken);
  const [crData, setCrData] = useState(null);
  const [consolidationData, setConsolidationData] = useState(null);
  const downloadedRef = useRef({ cr: false, consolidation: false });
  const resetCalledRef = useRef(false);
  const token = props.accessToken || accessToken;
  const crDataRef = useRef(crData);
  const consolidationDataRef = useRef(consolidationData);
  crDataRef.current = crData;
  consolidationDataRef.current = consolidationData;
  const shouldDownload =
    props.downloading && typeof props.downloading === 'object'
      ? Object.keys(props.downloading).length > 0
      : false;

  useEffect(() => {
    if (!props.projectId || !token) return;
    const projectId = props.projectId;
    downloadedRef.current = { cr: false, consolidation: false };
    resetCalledRef.current = false;
    Promise.all([
      fetch(`${API_ROOT}project-corporate-reports/${projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).then((r) => r.json()),
      fetch(
        `${API_ROOT}${
          CONSOLIDATION_API.data
        }/${projectId}?type=${encodeURIComponent('Consolidated')}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      ).then((r) => r.json()),
    ])
      .then(([crRes, consolidationRes]) => {
        if (get(crRes, 'statusCode') === 200) setCrData(crRes?.data);
        else customToast('error', crRes?.message || 'Failed to load CR report');
        if (get(consolidationRes, 'statusCode') === 200) {
          const consolidation = consolidationRes?.data;

          if (!isEmpty(consolidation.consolidatedReports)) {
            setConsolidationData(consolidation);
          } else {
            setConsolidationData(null);
          }
        } else
          customToast(
            'error',
            consolidationRes?.message || 'Failed to load consolidation report'
          );
        if (
          get(crRes, 'statusCode') !== 200 &&
          get(consolidationRes, 'statusCode') !== 200 &&
          props.resetData
        ) {
          props.resetData();
        }
      })
      .catch(() => {
        customToast('error', 'Failed to load reports');
        if (props.resetData) props.resetData();
      });
  }, [props.projectId, token]);

  const maybeReset = React.useCallback(() => {
    if (resetCalledRef.current || !props.resetData) return;
    const crDone = downloadedRef.current.cr || !crDataRef.current;
    const consolidationDone =
      downloadedRef.current.consolidation || !consolidationDataRef.current;
    if (crDone && consolidationDone) {
      resetCalledRef.current = true;
      props.resetData();
    }
  }, [props.resetData]);

  const handleConsolidationPdfReady = React.useCallback(
    (doc) => {
      if (!doc || downloadedRef.current.consolidation) return;
      const data = consolidationDataRef.current;
      if (!data) return;
      downloadedRef.current.consolidation = true;
      const projectCode = data?.projectInfo?.projectCode || 'consolidation';
      const fileName = `CR_Consolidation_Report_${projectCode}.pdf`;
      const blob = doc
        .setProperties({ title: 'CR Consolidation Report' })
        .output('blob', { filename: fileName });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      maybeReset();
    },
    [maybeReset]
  );

  const handleCrPdfReady = React.useCallback(
    (doc) => {
      if (!doc || downloadedRef.current.cr) return;
      const data = crDataRef.current;
      if (!data) return;
      downloadedRef.current.cr = true;
      const projectCode = data?.projectInfo?.projectCode || 'report';
      const fileName = `CR_Report_${projectCode}.pdf`;
      const blob = doc.setProperties({ title: 'CR Report' }).output('blob', {
        filename: fileName,
      });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      maybeReset();
    },
    [maybeReset]
  );

  return (
    <Container>
      <LinkContainer>
        {shouldDownload && crData && (
          <CRReportPDF
            corporateData={crData}
            pdfStoreKey="pdfDoc"
            onPdfReady={handleCrPdfReady}
          />
        )}
        {shouldDownload && consolidationData && (
          <CRConsolidationReportPDF
            consolidationData={consolidationData}
            pdfStoreKey="pdfDocConsolidation"
            onPdfReady={handleConsolidationPdfReady}
          />
        )}
      </LinkContainer>
    </Container>
  );
};

export default DualReportDownload;

const Container = styled.div`
  display: flex;
  align-items: center;
  visibility: hidden;
  height: 0;
  width: 0;
`;
const LinkContainer = styled.div`
  flex: 1 1 0px;
`;
